import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { currencyCodeSchema, journalEntryViewSchema, moneySchema, positiveAmountSchema, } from '../finance/finance.js';
import { equityHistoryItemSchema, equityPositionSchema, founderStatusSchema, } from '../founders/founders.js';
import { attachmentSchema, createLinkAttachmentRequestSchema } from '../governance/topics.js';
/**
 * Founder funding, founder debts and settlements (spec §7.12, §7.15–7.17, §18.1, ADR-0008). The
 * backend computes every requirement, balance and aging bucket deterministically; amounts travel as
 * decimal strings with their currency.
 */
// --- enums ----------------------------------------------------------------------------------------
export const FUNDING_CALL_STATUSES = [
    'DRAFT',
    'APPROVED',
    'PARTIALLY_FUNDED',
    'FUNDED',
    'CLOSED',
    'CANCELLED',
];
export const fundingCallStatusSchema = z.enum(FUNDING_CALL_STATUSES);
/** HYBRID exists in the schema but has no founder-approved rule yet (open decision 7). */
export const ALLOCATION_BASES = ['OWNERSHIP_PERCENTAGE', 'CUSTOM_COMMITMENTS', 'HYBRID'];
export const allocationBasisSchema = z.enum(ALLOCATION_BASES);
export const SUPPORTED_ALLOCATION_BASES = ['OWNERSHIP_PERCENTAGE', 'CUSTOM_COMMITMENTS'];
export const FUNDING_CONTRIBUTION_KINDS = [
    'FUNDING_CALL_PAYMENT',
    'DIRECT_EXPENSE_PAYMENT',
    'ADVANCE',
];
export const fundingContributionKindSchema = z.enum(FUNDING_CONTRIBUTION_KINDS);
export const CONTRIBUTION_CLASSIFICATIONS = [
    'PENDING_CLASSIFICATION',
    'FUNDING_CALL_SHARE',
    'CAPITAL_CONTRIBUTION',
    'FOUNDER_LOAN',
    'REIMBURSABLE_EXPENSE',
    'OTHER_APPROVED',
];
export const contributionClassificationSchema = z.enum(CONTRIBUTION_CLASSIFICATIONS);
/** Treatments a founder-paid expense can be classified into today (open decision 22). */
export const EXPENSE_CLASSIFICATIONS = [
    'REIMBURSABLE_EXPENSE',
    'FOUNDER_LOAN',
    'CAPITAL_CONTRIBUTION',
    'FUNDING_CALL_SHARE',
];
export const expenseClassificationSchema = z.enum(EXPENSE_CLASSIFICATIONS);
export const PARTY_TYPES = ['FOUNDER', 'COMPANY'];
export const partyTypeSchema = z.enum(PARTY_TYPES);
export const DEBT_ORIGINS = [
    'FUNDING_CALL_SHORTFALL',
    'FUNDING_CALL_COVER',
    'FOUNDER_ADVANCE',
    'FOUNDER_PAID_EXPENSE',
    'OVERPAYMENT_CREDIT',
    'ADJUSTMENT',
];
export const debtOriginSchema = z.enum(DEBT_ORIGINS);
export const DEBT_STATUSES = ['OPEN', 'PARTIALLY_SETTLED', 'SETTLED', 'REVERSED'];
export const debtStatusSchema = z.enum(DEBT_STATUSES);
export const SETTLEMENT_METHODS = [
    'CASH_PAYMENT',
    'BANK_TRANSFER',
    'NETTING_AGAINST_DISTRIBUTION',
    'WRITE_OFF',
    'OTHER_APPROVED',
];
export const settlementMethodSchema = z.enum(SETTLEMENT_METHODS);
/** Netting needs an approved netting policy (open decision 9) and distributions (prompt 08). */
export const RECORDABLE_SETTLEMENT_METHODS = [
    'CASH_PAYMENT',
    'BANK_TRANSFER',
    'WRITE_OFF',
    'OTHER_APPROVED',
];
export const recordableSettlementMethodSchema = z.enum(RECORDABLE_SETTLEMENT_METHODS);
/** Settlement methods that only a finalized founder decision can authorize. */
export const DECISION_SETTLEMENT_METHODS = [
    'NETTING_AGAINST_DISTRIBUTION',
    'WRITE_OFF',
    'OTHER_APPROVED',
];
export const DOCUMENT_STATUSES = ['RECORDED', 'REVERSED'];
export const documentStatusSchema = z.enum(DOCUMENT_STATUSES);
export const FOUNDER_LEDGER_ENTRY_TYPES = [
    'FUNDING_CALL_ALLOCATION',
    'CONTRIBUTION',
    'COVER_PROVIDED',
    'COVER_RECEIVED',
    'DEBT_INCURRED',
    'DEBT_SETTLED',
    'DISTRIBUTION_ALLOCATED',
    'DISTRIBUTION_PAID',
    'ADJUSTMENT',
    'REVERSAL',
];
export const founderLedgerEntryTypeSchema = z.enum(FOUNDER_LEDGER_ENTRY_TYPES);
export const FOUNDER_LEDGER_DIRECTIONS = ['OWED_BY_FOUNDER', 'OWED_TO_FOUNDER'];
export const founderLedgerDirectionSchema = z.enum(FOUNDER_LEDGER_DIRECTIONS);
export const FUNDING_NOTE_KINDS = ['NOTE', 'DISPUTE', 'RESOLUTION'];
export const fundingNoteKindSchema = z.enum(FUNDING_NOTE_KINDS);
export const FUNDING_NOTE_ENTITY_TYPES = [
    'FUNDING_CALL',
    'FOUNDER_CONTRIBUTION',
    'FOUNDER_DEBT',
    'SETTLEMENT',
];
export const fundingNoteEntityTypeSchema = z.enum(FUNDING_NOTE_ENTITY_TYPES);
/** Days past due (or since incurred when no due date): not due yet, 0–30, 31–60, 61–90, over 90. */
export const AGING_BUCKETS = [
    'NOT_DUE',
    'DAYS_0_30',
    'DAYS_31_60',
    'DAYS_61_90',
    'DAYS_OVER_90',
];
export const agingBucketSchema = z.enum(AGING_BUCKETS);
export const ALLOCATION_STATES = [
    'UNPAID',
    'PARTIALLY_PAID',
    'PAID',
    'CARRIED_AS_DEBT',
    'CANCELLED',
];
export const allocationStateSchema = z.enum(ALLOCATION_STATES);
// --- references -------------------------------------------------------------------------------------
export const founderRefSchema = z.object({ founderProfileId: uuidSchema, name: z.string() });
export const partySchema = z.object({
    type: partyTypeSchema,
    founderProfileId: uuidSchema.nullable(),
    /** Founder name, or "Company". */
    name: z.string(),
});
export const citedDecisionSchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
});
export const fundingCallRefSchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
});
export const bankAccountRefSchema = z.object({ id: uuidSchema, name: z.string() });
// --- notes and disputes -----------------------------------------------------------------------------
export const fundingNoteSchema = z.object({
    id: uuidSchema,
    kind: fundingNoteKindSchema,
    body: z.string(),
    author: z.string(),
    createdAt: isoDateTimeSchema,
    resolvesNoteId: uuidSchema.nullable(),
    /** For disputes: the resolution that closed it, if any. */
    resolution: z
        .object({
        noteId: uuidSchema,
        body: z.string(),
        author: z.string(),
        createdAt: isoDateTimeSchema,
    })
        .nullable(),
});
export const fundingNoteListSchema = z.object({ items: z.array(fundingNoteSchema) });
// --- funding calls ----------------------------------------------------------------------------------
export const fundingAllocationSchema = z.object({
    id: uuidSchema,
    founderProfileId: uuidSchema,
    founderName: z.string(),
    /** Ownership share used (percent, 6 decimals); null for custom commitments. */
    basisPercentage: z.string().nullable(),
    required: moneySchema,
    /** Paid by this founder towards their own share. */
    paidByFounder: moneySchema,
    /** Paid by other founders on this founder's behalf (each creates a debt of this founder). */
    coveredByOthers: moneySchema,
    outstanding: moneySchema,
    state: allocationStateSchema,
    overdue: z.boolean(),
    calculationNote: z.string().nullable(),
});
export const calculationLineSchema = z.object({
    founderProfileId: uuidSchema,
    founderName: z.string(),
    percentage: z.string().nullable(),
    /** Exact share before rounding (up to 10 decimals) — reproducible from the inputs. */
    exactAmount: z.string(),
    amount: z.string(),
    /** Rounding units added by the largest-remainder step. */
    roundingAdjustment: z.string(),
});
export const fundingCalculationSchema = z.object({
    basis: allocationBasisSchema,
    method: z.enum(['LARGEST_REMAINDER', 'CUSTOM_AMOUNTS']),
    roundingUnit: z.string(),
    total: moneySchema,
    recordDate: isoDateSchema,
    capTable: z
        .object({
        id: uuidSchema,
        versionNumber: z.number().int(),
        effectiveFrom: isoDateSchema,
        approvalDecisionId: uuidSchema.nullable(),
        approvalReference: z.string().nullable(),
    })
        .nullable(),
    lines: z.array(calculationLineSchema),
    explanation: z.string(),
});
export const fundingCallSummarySchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
    status: fundingCallStatusSchema,
    allocationBasis: allocationBasisSchema,
    total: moneySchema,
    applied: moneySchema,
    coveredByOthers: moneySchema,
    outstanding: moneySchema,
    recordDate: isoDateSchema,
    dueDate: isoDateSchema,
    overdue: z.boolean(),
    openDisputes: z.number().int(),
});
export const fundingCallPageSchema = cursorPageSchema(fundingCallSummarySchema);
// --- contributions ----------------------------------------------------------------------------------
export const contributionApplicationSchema = z.object({
    id: uuidSchema,
    allocationId: uuidSchema,
    fundingCall: fundingCallRefSchema,
    /** Founder whose share was paid. */
    founder: founderRefSchema,
    amount: moneySchema,
    isCover: z.boolean(),
    status: documentStatusSchema,
    /** The covered founder's debt to the payer (covers only). */
    debtId: uuidSchema.nullable(),
});
export const contributionSummarySchema = z.object({
    id: uuidSchema,
    founder: founderRefSchema,
    kind: fundingContributionKindSchema,
    classification: contributionClassificationSchema,
    fundingCall: fundingCallRefSchema.nullable(),
    amount: moneySchema,
    applied: moneySchema,
    /** Part not applied to any share — held as a credit owed to the founder. */
    unapplied: moneySchema,
    paidOn: isoDateSchema,
    paymentReference: z.string().nullable(),
    bankAccount: bankAccountRefSchema.nullable(),
    financialTransactionId: uuidSchema.nullable(),
    status: documentStatusSchema,
    reversedAt: isoDateTimeSchema.nullable(),
    reversalReason: z.string().nullable(),
    recordedBy: z.string(),
    recordedAt: isoDateTimeSchema,
});
export const contributionPageSchema = cursorPageSchema(contributionSummarySchema);
// --- debts and settlements --------------------------------------------------------------------------
export const settlementSchema = z.object({
    id: uuidSchema,
    debtId: uuidSchema,
    method: settlementMethodSchema,
    amount: moneySchema,
    settledOn: isoDateSchema,
    paymentReference: z.string().nullable(),
    bankAccount: bankAccountRefSchema.nullable(),
    decision: citedDecisionSchema.nullable(),
    note: z.string().nullable(),
    status: documentStatusSchema,
    reversedAt: isoDateTimeSchema.nullable(),
    reversalReason: z.string().nullable(),
    recordedBy: z.string(),
    recordedAt: isoDateTimeSchema,
    journalEntryId: uuidSchema.nullable(),
    evidence: z.array(attachmentSchema),
});
export const debtSummarySchema = z.object({
    id: uuidSchema,
    debtor: partySchema,
    creditor: partySchema,
    origin: debtOriginSchema,
    principal: moneySchema,
    settled: moneySchema,
    outstanding: moneySchema,
    status: debtStatusSchema,
    incurredOn: isoDateSchema,
    dueOn: isoDateSchema.nullable(),
    description: z.string(),
    fundingCall: fundingCallRefSchema.nullable(),
    contributionId: uuidSchema.nullable(),
    decision: citedDecisionSchema.nullable(),
    reason: z.string().nullable(),
    aging: agingBucketSchema.nullable(),
    daysOverdue: z.number().int().nullable(),
    openDisputes: z.number().int(),
});
export const debtPageSchema = cursorPageSchema(debtSummarySchema);
export const debtDetailSchema = debtSummarySchema.extend({
    settlements: z.array(settlementSchema),
    evidence: z.array(attachmentSchema),
    journalEntries: z.array(journalEntryViewSchema),
    notes: z.array(fundingNoteSchema),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
    reversedAt: isoDateTimeSchema.nullable(),
    reversalReason: z.string().nullable(),
    /** Whether a settlement moves company cash (company is a party) — then an account is required. */
    companyParty: z.boolean(),
    permissions: z.object({
        canSettle: z.boolean(),
        canSettleByDecision: z.boolean(),
        canReverse: z.boolean(),
        canReverseSettlements: z.boolean(),
        canComment: z.boolean(),
        canResolveDisputes: z.boolean(),
    }),
});
export const contributionDetailSchema = contributionSummarySchema.extend({
    applications: z.array(contributionApplicationSchema),
    debts: z.array(debtSummarySchema),
    evidence: z.array(attachmentSchema),
    journalEntries: z.array(journalEntryViewSchema),
    notes: z.array(fundingNoteSchema),
    permissions: z.object({ canReverse: z.boolean(), canComment: z.boolean() }),
});
export const fundingCallDetailSchema = fundingCallSummarySchema.extend({
    purpose: z.string(),
    approvalDecision: citedDecisionSchema.nullable(),
    approvalReference: z.string().nullable(),
    approvedAt: isoDateTimeSchema.nullable(),
    approvedBy: z.string().nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
    calculation: fundingCalculationSchema.nullable(),
    /** Draft only: the cap table or founders changed since the allocations were calculated. */
    allocationsStale: z.boolean(),
    allocations: z.array(fundingAllocationSchema),
    payments: z.array(contributionSummarySchema),
    debts: z.array(debtSummarySchema),
    notes: z.array(fundingNoteSchema),
    permissions: z.object({
        canEdit: z.boolean(),
        canApprove: z.boolean(),
        canCancel: z.boolean(),
        canClose: z.boolean(),
        canRecordPayment: z.boolean(),
        canReversePayments: z.boolean(),
        canComment: z.boolean(),
        canResolveDisputes: z.boolean(),
    }),
    version: z.number().int(),
});
// --- founder-paid expenses awaiting classification -------------------------------------------------
export const founderPaidExpenseSchema = z.object({
    transactionId: uuidSchema,
    transactionDate: isoDateSchema,
    founder: founderRefSchema,
    amount: moneySchema,
    counterpartyName: z.string().nullable(),
    category: z.string().nullable(),
    description: z.string().nullable(),
    /** The live classification, if any. */
    classification: z
        .object({ contributionId: uuidSchema, classification: contributionClassificationSchema })
        .nullable(),
});
export const founderPaidExpenseListSchema = z.object({ items: z.array(founderPaidExpenseSchema) });
// --- founder ledger and personal finance cabinet ----------------------------------------------------
export const founderLedgerEntrySchema = z.object({
    id: uuidSchema,
    entryDate: isoDateSchema,
    entryType: founderLedgerEntryTypeSchema,
    direction: founderLedgerDirectionSchema,
    amount: moneySchema,
    description: z.string(),
    reason: z.string().nullable(),
    fundingCall: fundingCallRefSchema.nullable(),
    contributionId: uuidSchema.nullable(),
    debtId: uuidSchema.nullable(),
    settlementId: uuidSchema.nullable(),
    journalEntryId: uuidSchema.nullable(),
    reversesEntryId: uuidSchema.nullable(),
    reversed: z.boolean(),
    createdBy: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    /** Running balance in this currency after the entry: positive = the founder owes, negative = owed to them. */
    balanceAfter: moneySchema,
});
export const founderLedgerPageSchema = cursorPageSchema(founderLedgerEntrySchema);
export const founderBalanceSchema = z.object({
    currency: z.string(),
    /** Unpaid shares of open funding calls + debts where the founder is the debtor. */
    owedByFounder: z.string(),
    /** Debts where the founder is the creditor (covers given, credits, reimbursements, loans). */
    owedToFounder: z.string(),
    /** owedByFounder − owedToFounder: positive = the founder owes. */
    net: z.string(),
    outstandingContributions: z.string(),
    overdueContributions: z.string(),
    /** Net balance of the founder ledger; equals `net` (shown for reconciliation). */
    ledgerBalance: z.string(),
    contributed: z.string(),
    distributionsAllocated: z.string(),
    distributionsPaid: z.string(),
});
export const openAllocationSchema = fundingAllocationSchema.extend({
    fundingCall: fundingCallRefSchema,
    dueDate: isoDateSchema,
    daysOverdue: z.number().int().nullable(),
});
export const founderFinanceSchema = z.object({
    asOf: isoDateSchema,
    founder: z.object({
        profileId: uuidSchema,
        name: z.string(),
        status: founderStatusSchema,
    }),
    isSelf: z.boolean(),
    equity: z.object({
        current: equityPositionSchema.nullable(),
        history: z.array(equityHistoryItemSchema),
    }),
    balances: z.array(founderBalanceSchema),
    openAllocations: z.array(openAllocationSchema),
    debtsOwedByFounder: z.array(debtSummarySchema),
    debtsOwedToFounder: z.array(debtSummarySchema),
    contributions: z.array(contributionSummarySchema),
    settlements: z.array(settlementSchema.extend({
        debtor: partySchema,
        creditor: partySchema,
        origin: debtOriginSchema,
    })),
    openDisputes: z.number().int(),
});
// --- dashboard --------------------------------------------------------------------------------------
export const agingAmountsSchema = z.object({
    notDue: z.string(),
    days0to30: z.string(),
    days31to60: z.string(),
    days61to90: z.string(),
    over90: z.string(),
});
export const fundingOverviewSchema = z.object({
    asOf: isoDateSchema,
    period: z.object({ from: isoDateSchema, to: isoDateSchema }),
    /** Calls with a due date in the period (cancelled calls excluded), per currency. */
    totals: z.array(z.object({
        currency: z.string(),
        required: z.string(),
        paidByFounders: z.string(),
        coveredByOthers: z.string(),
        outstanding: z.string(),
        /** Unpaid shares of closed calls, now founders' debts to the company. */
        carriedAsDebt: z.string(),
        overdue: z.string(),
        calls: z.number().int(),
    })),
    founders: z.array(z.object({
        founder: founderRefSchema,
        currency: z.string(),
        owes: z.string(),
        isOwed: z.string(),
        net: z.string(),
        /** What the founder owes, by days past due. */
        aging: agingAmountsSchema,
    })),
    overdueContributions: z.array(z.object({
        fundingCall: fundingCallRefSchema,
        founder: founderRefSchema,
        outstanding: moneySchema,
        dueDate: isoDateSchema,
        daysOverdue: z.number().int(),
    })),
    openCalls: z.array(fundingCallSummarySchema),
    pendingClassification: z.array(founderPaidExpenseSchema),
    openDisputes: z.number().int(),
    permissions: z.object({
        canCreateCall: z.boolean(),
        canRecord: z.boolean(),
        canManage: z.boolean(),
    }),
});
export const fundingOverviewQuerySchema = z
    .object({ from: isoDateSchema.optional(), to: isoDateSchema.optional() })
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, {
    message: 'The period must end on or after its start',
    path: ['to'],
});
// --- requests ---------------------------------------------------------------------------------------
/** Funding requirements are split at cent precision, so totals and custom amounts use ≤ 2 decimals. */
export const callAmountSchema = z
    .string()
    .trim()
    .regex(/^\d{1,16}(\.\d{1,4})?$/, 'Enter an amount such as 1250.50, with at most 2 decimals')
    .refine((value) => /[1-9]/.test(value), { message: 'The amount must be greater than zero' })
    .refine((value) => !/\.\d{3,}$/.test(value.replace(/0+$/, '')), {
    message: 'Use at most 2 decimals for funding amounts',
});
const optionalReference = z.string().trim().max(500).nullable().default(null);
const fundingCallFields = {
    title: z.string().trim().min(1, 'Enter a title').max(200),
    purpose: z.string().trim().min(1, 'Describe what the money is for').max(4000),
    totalAmount: callAmountSchema,
    currency: currencyCodeSchema,
    recordDate: isoDateSchema,
    dueDate: isoDateSchema,
    allocationBasis: z.enum(SUPPORTED_ALLOCATION_BASES),
    /** Required (and only allowed) for CUSTOM_COMMITMENTS: one amount per founder, summing to the total. */
    customAllocations: z
        .array(z.object({ founderProfileId: uuidSchema, amount: callAmountSchema }))
        .max(50)
        .default([]),
    approvalDecisionId: uuidSchema.nullable().default(null),
    approvalReference: optionalReference,
};
function callIsConsistent(value, ctx) {
    if (value.dueDate < value.recordDate) {
        ctx.addIssue({
            code: 'custom',
            message: 'The due date cannot be before the record date',
            path: ['dueDate'],
        });
    }
    if (value.allocationBasis === 'CUSTOM_COMMITMENTS' && value.customAllocations.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'Enter the amount each founder commits',
            path: ['customAllocations'],
        });
    }
    if (value.allocationBasis === 'OWNERSHIP_PERCENTAGE' && value.customAllocations.length > 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'Ownership-based calls are calculated from the cap table',
            path: ['customAllocations'],
        });
    }
    const ids = value.customAllocations.map((item) => item.founderProfileId);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: 'custom',
            message: 'Each founder can appear only once',
            path: ['customAllocations'],
        });
    }
}
export const createFundingCallRequestSchema = z
    .object(fundingCallFields)
    .superRefine(callIsConsistent);
export const updateFundingCallRequestSchema = z
    .object({ ...fundingCallFields, version: versionSchema })
    .superRefine(callIsConsistent);
export const approveFundingCallRequestSchema = z
    .object({
    version: versionSchema,
    approvalDecisionId: uuidSchema.nullable().default(null),
    approvalReference: optionalReference,
})
    .refine((value) => value.approvalDecisionId !== null || (value.approvalReference ?? '').trim().length >= 3, {
    message: 'Cite the finalized decision or the approval reference (e.g. signed minutes)',
    path: ['approvalReference'],
});
export const cancelFundingCallRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const closeFundingCallRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
    /** Due date of the debts created for unpaid shares (optional). */
    shortfallDueOn: isoDateSchema.nullable().default(null),
});
const evidenceList = z.array(createLinkAttachmentRequestSchema).max(10);
export const recordPaymentRequestSchema = z
    .object({
    founderProfileId: uuidSchema,
    kind: z.enum(['FUNDING_CALL_PAYMENT', 'ADVANCE']),
    fundingCallId: uuidSchema.nullable().default(null),
    amount: positiveAmountSchema,
    currency: currencyCodeSchema,
    paidOn: isoDateSchema,
    bankAccountId: uuidSchema,
    paymentReference: z.string().trim().max(200).nullable().default(null),
    evidence: evidenceList.min(1, 'Add a link to the bank confirmation or receipt'),
    /**
     * How the payment is applied to shares of the call. `null` = automatic: the payer's own unpaid
     * share first. Applying to another founder's share records a cover and that founder's debt.
     * Anything not applied becomes a credit owed to the payer (never redistributed silently).
     */
    applications: z
        .array(z.object({ allocationId: uuidSchema, amount: positiveAmountSchema }))
        .max(50)
        .nullable()
        .default(null),
})
    .superRefine((value, ctx) => {
    if (value.kind === 'FUNDING_CALL_PAYMENT' && value.fundingCallId === null) {
        ctx.addIssue({ code: 'custom', message: 'Choose the funding call', path: ['fundingCallId'] });
    }
    if (value.kind === 'ADVANCE' && (value.fundingCallId !== null || value.applications !== null)) {
        ctx.addIssue({
            code: 'custom',
            message: 'An advance is not paid towards a funding call',
            path: ['fundingCallId'],
        });
    }
    const ids = (value.applications ?? []).map((item) => item.allocationId);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: 'custom',
            message: 'Apply to each share only once',
            path: ['applications'],
        });
    }
});
export const reverseRequestSchema = z.object({ reason: reasonSchema });
export const classifyFounderPaidRequestSchema = z
    .object({
    classification: expenseClassificationSchema,
    /** FUNDING_CALL_SHARE: the call whose share the expense pays. */
    fundingCallId: uuidSchema.nullable().default(null),
    decisionId: uuidSchema.nullable().default(null),
    reason: reasonSchema,
    /** Repayment due date for reimbursements and loans (optional). */
    dueOn: isoDateSchema.nullable().default(null),
})
    .superRefine((value, ctx) => {
    if ((value.classification === 'FUNDING_CALL_SHARE') !== (value.fundingCallId !== null)) {
        ctx.addIssue({
            code: 'custom',
            message: value.classification === 'FUNDING_CALL_SHARE'
                ? 'Choose the funding call this expense pays towards'
                : 'Only a funding-call share names a call',
            path: ['fundingCallId'],
        });
    }
});
export const createAdjustmentRequestSchema = z
    .object({
    debtorFounderProfileId: uuidSchema,
    creditorFounderProfileId: uuidSchema,
    amount: positiveAmountSchema,
    currency: currencyCodeSchema,
    incurredOn: isoDateSchema,
    dueOn: isoDateSchema.nullable().default(null),
    description: z.string().trim().min(3, 'Describe the adjustment').max(500),
    reason: reasonSchema,
    decisionId: uuidSchema.nullable().default(null),
    evidence: evidenceList.min(1, 'Add a link to the evidence for this adjustment'),
})
    .superRefine((value, ctx) => {
    if (value.debtorFounderProfileId === value.creditorFounderProfileId) {
        ctx.addIssue({
            code: 'custom',
            message: 'A founder cannot owe themselves',
            path: ['creditorFounderProfileId'],
        });
    }
    if (value.dueOn !== null && value.dueOn < value.incurredOn) {
        ctx.addIssue({
            code: 'custom',
            message: 'The due date cannot be before the date incurred',
            path: ['dueOn'],
        });
    }
});
export const recordSettlementRequestSchema = z
    .object({
    method: recordableSettlementMethodSchema,
    amount: positiveAmountSchema,
    settledOn: isoDateSchema,
    /** Company account the money moved through — required when the company pays or is paid. */
    bankAccountId: uuidSchema.nullable().default(null),
    paymentReference: z.string().trim().max(200).nullable().default(null),
    decisionId: uuidSchema.nullable().default(null),
    note: z.string().trim().max(2000).nullable().default(null),
    evidence: evidenceList.default([]),
})
    .superRefine((value, ctx) => {
    const byDecision = DECISION_SETTLEMENT_METHODS.includes(value.method);
    if (byDecision && value.decisionId === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'A write-off or other treatment needs a finalized founder decision',
            path: ['decisionId'],
        });
    }
    if (byDecision && value.bankAccountId !== null) {
        ctx.addIssue({
            code: 'custom',
            message: 'No money moves in a write-off',
            path: ['bankAccountId'],
        });
    }
    if (!byDecision && value.evidence.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'Add a link to the payment confirmation',
            path: ['evidence'],
        });
    }
});
export const createFundingNoteRequestSchema = z.object({
    entityType: fundingNoteEntityTypeSchema,
    entityId: uuidSchema,
    kind: z.enum(['NOTE', 'DISPUTE']),
    body: z.string().trim().min(1, 'Write the note').max(4000),
});
export const resolveDisputeRequestSchema = z.object({
    body: z.string().trim().min(3, 'Explain how the dispute was resolved').max(4000),
});
// --- queries ----------------------------------------------------------------------------------------
const csvEnumList = (values, message) => z
    .string()
    .optional()
    .transform((value, ctx) => {
    if (value === undefined || value === '')
        return undefined;
    const parsed = z.array(z.enum(values)).safeParse(value.split(','));
    if (!parsed.success) {
        ctx.addIssue({ code: 'custom', message });
        return z.NEVER;
    }
    return parsed.data;
});
export const fundingCallQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    status: csvEnumList(FUNDING_CALL_STATUSES, 'Unknown funding call status'),
});
export const debtQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    status: csvEnumList(DEBT_STATUSES, 'Unknown debt status'),
    founderProfileId: uuidSchema.optional(),
});
export const fundingContributionQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    founderProfileId: uuidSchema.optional(),
    fundingCallId: uuidSchema.optional(),
});
export const founderLedgerQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    currency: currencyCodeSchema.optional(),
});
//# sourceMappingURL=funding.js.map